import { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useFormDataStore } from "../store/formDataStore";
import { getEvidenceData } from "@/features/reports/api/reportApi";
import {
  incidentDataQuery,
  reportMetadataQuery,
} from "../../../features/reports/api/reportQueries";
import { useResolvedReportSchema } from "../../../features/reports/hooks/useResolvedReportSchema";
import { VideoData } from "../../../shared/interfaces/VideoData";
import { useToast } from "../../../shared/toast";
import { ReportStatus } from "../../../shared/interfaces/ReportStatus";
import { coReviewKeys } from "@/features/co-review/api/coReviewQueries";
import { useCoComments } from "@/features/co-review/hooks/useCoComments";
import { useCoApprovalEnabled } from "@/features/co-review/hooks/useCoApprovalEnabled";

// Exported so other pages (e.g. FinalEdits' deep-link gate) gate on the
// exact same list — divergence causes redirect loops.
export const EDITABLE_STATUSES = [
  ReportStatus.PENDING_REVIEW,
  ReportStatus.CHANGES_REQUESTED,
];

/**
 * The editor's data layer, on react-query (metadata + incident queries, the
 * shared schema-resolution hook) with the side effects kept imperative. The
 * returned API is unchanged — the values below stay local state because the
 * WebSocket layer mutates them through the setters (video frames, reportType
 * pushes, showRMSCommit updates).
 *
 * Ordering contract (the old Promise.allSettled serialization, preserved):
 * the metadata effect decides the non-editable redirect BEFORE the incident
 * effect may initStore — the incident effect waits for metadata success and
 * checks the redirect flag; effects within one commit run in declaration
 * order.
 */
export const useReportData = () => {
  const { reportID } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const coApprovalEnabled = useCoApprovalEnabled();

  const [videoData, setVideoData] = useState<VideoData | VideoData[] | null>(
    null,
  );
  const [videoLoadStatus, setVideoLoadStatus] = useState<
    "loading" | "loaded" | "error"
  >("loading");
  const [showRMSCommit, setShowRMSCommit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [reportType, setReportType] = useState<string>("");
  const [lastModifiedTime, setLastModifiedTime] = useState<Date | null>(null);
  const [enableEditing, setEnableEditing] = useState<boolean>(false);
  const [rms, setRms] = useState<string>("");
  const [reportStatus, setReportStatus] = useState<string>("");
  const [coReturnedAt, setCoReturnedAt] = useState<string | null>(null);
  const [reportOwnerUserId, setReportOwnerUserId] = useState<string | null>(null);

  // True once the non-editable redirect fired — nothing below may touch the
  // form store afterwards.
  const redirectedRef = useRef(false);
  // First incident arrival inits the store (clearing undo history); later
  // arrivals (the narrativeUpdated refetch) setFormData, preserving undo.
  const incidentInitializedRef = useRef(false);
  // The lock toast fires once per mount (the old code only toasted on the
  // initial fetch, never on refetches).
  const lockToastShownRef = useRef(false);
  // Evidence fan-out runs once per mount (the old reFetch skipped it).
  const evidenceFetchedRef = useRef(false);

  const metadataQuery = useQuery({
    ...reportMetadataQuery(reportID ?? ""),
    enabled: !!reportID,
  });
  const incidentQuery = useQuery({
    ...incidentDataQuery(reportID ?? ""),
    enabled: !!reportID,
  });

  // (1) Metadata: redirect gate + derived review state.
  const metadata = metadataQuery.data;
  useEffect(() => {
    if (!metadata || !reportID) return;

    if (!EDITABLE_STATUSES.includes(metadata.reportStatus as ReportStatus)) {
      redirectedRef.current = true;
      navigate(`/preview-report-info/${reportID}`);
      return;
    }

    setReportStatus(metadata.reportStatus);
    setShowRMSCommit(![null, "", "KAR"].includes(metadata.rms));
    setEnableEditing(metadata.canEdit);
    setRms(metadata.rms || "KAR");
    setCoReturnedAt(
      metadata.reportStatus === ReportStatus.CHANGES_REQUESTED
        ? (metadata.reviewedAt ?? metadata.updatedAt ?? null)
        : null,
    );
    setReportOwnerUserId(metadata.userId);

    if (!metadata.canEdit && !lockToastShownRef.current) {
      lockToastShownRef.current = true;
      toast.warning(
        "This report is currently locked for editing because another user generated it. You can view and freely navigate through all sections, but editing is disabled. If you believe you should have access, please contact your system administrator.",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata, reportID, navigate]);

  // (2) Incident: store init on first arrival, setFormData on refetches.
  // Gated on metadata success so the redirect decision always runs first.
  const incidentData = incidentQuery.data;
  const incidentUpdatedAt = incidentQuery.dataUpdatedAt;
  useEffect(() => {
    if (!incidentData || !metadataQuery.isSuccess || redirectedRef.current) {
      return;
    }
    setLastModifiedTime(
      incidentData.updatedAt ? new Date(incidentData.updatedAt) : new Date(),
    );
    setReportType(incidentData.reportType);
    const store = useFormDataStore.getState();
    if (!incidentInitializedRef.current) {
      incidentInitializedRef.current = true;
      store.initStore(incidentData.reportContent, 0);
    } else {
      store.setFormData(incidentData.reportContent);
    }
  }, [incidentData, incidentUpdatedAt, metadataQuery.isSuccess]);

  // (3) Evidence fan-out: once per mount, off the metadata's evidence ids.
  const evidenceIds = metadata?.evidenceIds;
  useEffect(() => {
    if (!evidenceIds || redirectedRef.current || evidenceFetchedRef.current) {
      return;
    }
    evidenceFetchedRef.current = true;
    let cancelled = false;
    Promise.all(evidenceIds.map((id: string) => getEvidenceData(id)))
      .then((evidenceResults) => {
        if (cancelled) return;
        setVideoData(evidenceResults);
        setVideoLoadStatus("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching evidence data:", err);
        setVideoLoadStatus("error");
        toast.error(
          "An error occurred while fetching evidence data. Please try again later.",
        );
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidenceIds]);

  // (4) CO comments: read from the shared query cache ("current" cycle),
  // mutated through the co-review feature's optimistic mutations. The status
  // gate doubles as the redirect gate — reportStatus is only ever set after
  // the metadata effect decided against redirecting. useCoComments yields []
  // whenever disabled or errored (the disabled-observer and error-retains-
  // data traps live there, not here).
  const coCommentsEnabled =
    !!reportID &&
    coApprovalEnabled &&
    reportStatus === ReportStatus.CHANGES_REQUESTED;
  const { comments: coComments, error: coCommentsError } = useCoComments({
    reportId: reportID,
    cycle: "current",
    enabled: coCommentsEnabled,
  });

  // Refresh alongside every metadata arrival (the old code fetched them on
  // both the initial load and reFetch).
  const metadataUpdatedAt = metadataQuery.dataUpdatedAt;
  useEffect(() => {
    if (!reportID || !metadataUpdatedAt) return;
    queryClient.invalidateQueries({
      queryKey: coReviewKeys.comments(reportID),
    });
  }, [reportID, metadataUpdatedAt, queryClient]);

  useEffect(() => {
    if (!coCommentsError) return;
    console.error("Error fetching CO comments:", coCommentsError);
    toast.error(
      "An error occurred while loading CO review comments. Please refresh to try again.",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coCommentsError]);

  // (5) Fatal errors: destroy the store only when the initial load failed
  // (a failed refetch toasts and bails to home but keeps the store, exactly
  // like the old reFetch catch).
  const fatalError = metadataQuery.error || incidentQuery.error;
  useEffect(() => {
    if (!fatalError) return;
    // The non-editable preview redirect takes precedence: when metadata
    // already bounced the user, a parallel incident-fetch failure must not
    // override it with a home redirect + error toast (old serialized
    // ordering).
    if (redirectedRef.current) return;
    if (!incidentInitializedRef.current) {
      useFormDataStore.getState().destroyStore();
    }
    toast.error(
      (isAxiosError(fatalError) && fatalError.response?.data?.error) ||
        "An error occurred while fetching extracted incident information. Please try again later.",
    );
    navigate("/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fatalError]);

  // Schema: the shared editor-parity pipeline (full resolve + oneOf→enum).
  const schemaHook = useResolvedReportSchema({
    enabled: !!reportType,
    reportType,
    rms: rms || null,
    incident: null,
    karSlice: false,
    oneOfToEnum: true,
    refFilter: "all",
  });
  const jsonSchema = schemaHook.jsonSchema;
  useEffect(() => {
    if (jsonSchema) setLoading(false);
  }, [jsonSchema]);
  useEffect(() => {
    if (!schemaHook.isError) return;
    // Without this a failed schema fetch left `loading` true forever and the
    // edit page showed its skeleton indefinitely. Mirror the hook's fatal
    // behavior: return home with an error toast.
    console.error("Failed to load report schema:", schemaHook.error);
    toast.error(
      (isAxiosError(schemaHook.error) &&
        schemaHook.error.response?.data?.error) ||
        "An error occurred while loading the report form. Please try again later.",
    );
    navigate("/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaHook.isError]);

  const reFetchExtractedIncident = useCallback(async () => {
    if (!reportID) return;
    // The effects above translate fresh data into store/state updates; the
    // metadata effect re-checks the redirect gate.
    await Promise.all([metadataQuery.refetch(), incidentQuery.refetch()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportID, metadataQuery.refetch, incidentQuery.refetch]);

  return {
    reportID,
    videoData,
    setVideoData,
    videoLoadStatus,
    showRMSCommit,
    setShowRMSCommit,
    loading,
    setLoading,
    jsonSchema,
    reportType,
    setReportType,
    lastModifiedTime,
    setLastModifiedTime,
    enableEditing,
    setEnableEditing,
    reFetchExtractedIncident,
    reportRms: rms,
    reportStatus,
    coComments,
    coReturnedAt,
    reportOwnerUserId,
  };
};
